import React, { createContext, useState, useContext, useEffect } from 'react';

const AdminContext = createContext();

export const AdminProvider = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Check if already authenticated on mount
  useEffect(() => {
    checkSession();
  }, []);

  // Set up keyboard listener for Ctrl+Shift+A / Cmd+Shift+A
  useEffect(() => {
    const handleKeyPress = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const isCorrectModifier = isMac ? e.metaKey : e.ctrlKey;

      if (isCorrectModifier && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        setShowLoginModal(true);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  const checkSession = async () => {
    try {
      const response = await fetch('/api/auth', {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        setIsAdmin(true);
      } else {
        setIsAdmin(false);
      }
    } catch (error) {
      console.error('Error checking admin session:', error);
      setIsAdmin(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await fetch('/api/auth', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ username, password }),
      });

      if (response.ok) {
        setIsAdmin(true);
        setShowLoginModal(false);
        return { success: true };
      } else {
        const data = await response.json();
        return {
          success: false,
          error: data.message || 'Invalid password'
        };
      }
    } catch (error) {
      console.error('Error logging in:', error);
      return {
        success: false,
        error: 'Failed to authenticate'
      };
    }
  };

  const logout = async () => {
    try {
      await fetch('/api/auth', {
        method: 'DELETE',
        credentials: 'include',
      });

      setIsAdmin(false);
      setShowLoginModal(false);
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const value = {
    isAdmin,
    setIsAdmin,
    login,
    logout,
    checkSession,
    showLoginModal,
    setShowLoginModal,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};
